import {
  buildApiUrl,
  buildQueryParams,
  getApiHeaders,
  refreshOn401,
} from './queriesHelpers';

const api = process.env.REACT_APP_API;

interface RequestOptions {
  token?: string | null;
  credentials?: RequestCredentials;
  page?: number;
  size?: number;
  pagination?: boolean;
  body?: any;
  includePageSize?: boolean;
  signal?: AbortSignal;
}

interface IDItem {
  id?: number | string;
  Id?: number | string;
  [key: string]: any;
}

const fetchItem = async (
  endpoint: string,
  params: Record<string, any> = {},
  options: RequestOptions = {},
) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const { token = null, credentials = 'include', signal } = options;

  const url = buildApiUrl(endpoint, params, true);

  const response = await fetch(url, {
    headers: getApiHeaders(token, 'GET', endpoint),
    credentials: credentials as RequestCredentials,
    signal,
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to fetch item:', response.statusText);
    throw new Error(response.statusText);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Received non-JSON response');
  }

  return response.json();
};

const fetchItems = async (
  endpoint: string,
  params: Record<string, any> = {},
  options: RequestOptions = {},
) => {
  if (!endpoint) {
    return { items: [], total_count: 0 };
  }

  const {
    page = 1,
    size = 1000,
    token = null,
    credentials = 'include',
    pagination = false,
    body = null,
    includePageSize = true,
    signal,
  } = options;

  const queryParamsObj = includePageSize
    ? { page, size, ...params }
    : { ...params };

  const url = buildApiUrl(endpoint, queryParamsObj, Boolean(body));

  const response = await fetch(url, {
    headers: getApiHeaders(token, body ? 'POST' : 'GET', endpoint),
    credentials: credentials as RequestCredentials,
    signal,
    ...(body ? { body: JSON.stringify(body), method: 'POST' as const } : {}),
  });

  if (response.status === 401) {
    await refreshOn401(endpoint);
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to fetch items:', response.statusText);
    throw new Error(response.statusText);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Received non-JSON response');
  }

  const data = await response.json();

  if (pagination) return data;
  if (data && typeof data === 'object') {
    if ('items' in data) return (data as any).items;
    if ('records' in data) return (data as any).records;
  }
  return data;
};

const FETCH_ALL_PAGE_SIZE = 1000;
const FETCH_ALL_MAX_PAGES = 100;

/**
 * Every row of a list endpoint. A list call with no `size` gets the server's default page (25),
 * so a screen that needs the whole set - an audit, a "who is already assigned" check - has to
 * walk the pages itself. Stops on the first short page; rows are de-duplicated by id in case the
 * set shifts between requests.
 */
const fetchAllItems = async (
  endpoint: string,
  params: Record<string, any> = {},
  options: RequestOptions = {},
) => {
  const rows: any[] = [];
  const seen = new Set<unknown>();

  for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
    const batch = await fetchItems(endpoint, params, {
      ...options,
      page,
      size: FETCH_ALL_PAGE_SIZE,
      includePageSize: true,
    });

    if (!Array.isArray(batch)) return batch;

    for (const row of batch) {
      const id = row && typeof row === 'object' ? (row as any).id : undefined;
      if (id !== undefined) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      rows.push(row);
    }

    if (batch.length < FETCH_ALL_PAGE_SIZE) break;
  }

  return rows;
};

const fetchStream = async (
  endpoint: string,
  body: any,
  tokenOrOptions: string | null | RequestOptions = null,
) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  let token: string | null = null;
  let credentials: RequestCredentials = 'include';
  let signal: AbortSignal | undefined = undefined;

  if (tokenOrOptions && typeof tokenOrOptions === 'object') {
    token = tokenOrOptions.token ?? null;
    credentials = tokenOrOptions.credentials ?? 'include';
    signal = tokenOrOptions.signal;
  } else {
    token = (tokenOrOptions as string | null) ?? null;
  }

  const url = buildApiUrl(endpoint, {}, true);

  const resp = await fetch(url, {
    method: 'POST',
    headers: getApiHeaders(token, 'POST', endpoint),
    body: JSON.stringify(body),
    credentials,
    signal,
  });

  if (resp.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }

  if (!resp.ok) {
    let message = resp.statusText;
    try {
      const text = await resp.text();
      if (text) message = text;
    } catch {
      // ignore text parse errors
    }
    throw new Error(message || 'Streaming request failed');
  }

  if (!resp.body) {
    throw new Error('Streaming not supported by browser/response');
  }

  return resp;
};

/**
 * The most specific message a failed response carries: DRF/Go bodies put it in `detail`, `error`,
 * `message` or a per-field list. Falls back to the status text when the body is empty or not JSON.
 */
const failureMessage = async (response: Response): Promise<string> => {
  try {
    if (typeof response.json === 'function') {
      const body: any = await response.json();
      const pick = (value: any): string | null => {
        if (typeof value === 'string' && value.trim()) return value;
        if (Array.isArray(value) && value.length) return pick(value[0]);
        return null;
      };
      const direct = pick(body?.detail) ?? pick(body?.error) ?? pick(body?.message);
      if (direct) return direct;
      if (body && typeof body === 'object') {
        for (const value of Object.values(body)) {
          const found = pick(value);
          if (found) return found;
        }
      }
    }
  } catch {
    // body was empty or not JSON
  }
  return response.statusText;
};

const updateItem = async <T extends IDItem>(
  endpoint: string,
  method: 'PUT' | 'PATCH',
  item: T,
  params: Record<string, any> | null = null,
  token: string | null = null,
) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const itemId = item.id ?? item.Id;
  if (!itemId) {
    throw new Error('Item does not have an id or Id property');
  }

  const baseUrl = api;
  const targetEndpoint = `${baseUrl}/${endpoint}/${itemId}/`;
  const url = params ? `${targetEndpoint}?${buildQueryParams(params)}` : targetEndpoint;

  const payload = { ...item };
  if (method === 'PATCH' && payload.Id !== undefined) {
    delete payload.Id;
  }

  const response = await fetch(url, {
    headers: getApiHeaders(token, method, endpoint),
    method,
    body: ['PUT', 'PATCH'].includes(method) ? JSON.stringify(payload) : undefined,
    credentials: 'include',
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to update item:', response.statusText);
    throw new Error(await failureMessage(response));
  }

  return response.json();
};

const putItem = async <T extends IDItem>(
  endpoint: string,
  item: T,
  params: Record<string, any> | null = null,
) => updateItem(endpoint, 'PUT', item, params);

const patchItem = async <T extends IDItem>(
  endpoint: string,
  item: T,
  params: Record<string, any> | null = null,
) => updateItem(endpoint, 'PATCH', item, params);

const postItem = async <T>(
  endpoint: string,
  item: T,
  params: Record<string, any> = {},
  token: string | null = null,
) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const url = buildApiUrl(endpoint, params, true);

  const response = await fetch(url, {
    headers: getApiHeaders(token, 'POST', endpoint),
    method: 'POST',
    body: JSON.stringify(item),
    credentials: 'include',
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to post item:', response.statusText);
    throw new Error(await failureMessage(response));
  }

  return response.json();
};

const deleteItem = async (endpoint: string, token: string | null = null) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const url = buildApiUrl(endpoint);
  const response = await fetch(url, {
    headers: getApiHeaders(token, 'DELETE', endpoint),
    method: 'DELETE',
    credentials: 'include',
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    console.error('Failed to delete item:', response.statusText);
    throw new Error(await failureMessage(response));
  }

  return response.ok;
};

const putItemRaw = async (endpoint: string, payload: any) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const url = `${process.env.REACT_APP_API}/${endpoint}`;
  const response = await fetch(url, {
    headers: getApiHeaders(),
    method: 'PUT',
    body: JSON.stringify(payload),
    credentials: 'include',
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const patchItemRaw = async (endpoint: string, payload: any) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const url = `${process.env.REACT_APP_API}/${endpoint}`;
  const response = await fetch(url, {
    headers: getApiHeaders(),
    method: 'PATCH',
    body: JSON.stringify(payload),
    credentials: 'include',
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const postItemRaw = async (endpoint: string, payload: any) => {
  if (!endpoint) {
    throw new Error('Endpoint is required');
  }

  const url = `${process.env.REACT_APP_API}/${endpoint}`;
  const response = await fetch(url, {
    headers: getApiHeaders(),
    method: 'POST',
    body: JSON.stringify(payload),
    credentials: 'include',
  });

  if (response.status === 401) {
    await refreshOn401();
    throw new Error('Unauthorized');
  }
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

export {
  fetchItem,
  fetchItems,
  fetchAllItems,
  fetchStream,
  putItem,
  patchItem,
  postItem,
  deleteItem,
  putItemRaw,
  patchItemRaw,
  postItemRaw,
  getApiHeaders,
};